import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { SelectionItem } from '../../component/select/select.component';
import { PagedResult } from '../../model/paged-result';
import { MessageService } from '../message.service';
import { ProgressBarService } from '../progress-bar.service';
import { ModelBase } from './../../model/model-base';
import { DataServiceBase } from './data.service.base';
import { IAppConfig } from '../../../app.config';

/**
 * Create a model from a "model-like".
 * @param modelType Constructor / Type
 * @param modelLike Data to map to model type.
 * @returns A new model instance.
 */
export function toModel<TModelType extends new (...args: any) => {}>(
  modelType: TModelType,
  modelLike: InstanceType<TModelType>
): InstanceType<TModelType> {
  try {
    return Object.assign(new modelType(), modelLike);
    // WARNING : This doesn't work if the the object contains inner objects - The inheritance gets fucked up, function calls will fail!
    //           Basically if class Model { inner: OtherModel } then after the above call model.inner.prototype will be Object INSTEAD OF OtherModel !!
  } catch (e) {
    console.error(e);
    throw new Error('Invalid data format.');
  }
}

export declare interface IDelete {
  delete(id: string): Observable<any>;
}

/** Any type with an 'id: string' value */
export interface IModel {
  id: string;
}

/**
 * In modern implementations TModel is used for GET, TModelCreate for POST, TModelUpdate for PUT/PATCH.
 * In legacy implementations TModel is used for everything.
 */
export abstract class DataService<
    TModel extends ModelBase,
    TModelCreate = TModel,
    TModelUpdate extends IModel = TModel,
    TUpdateResponse = any,
    TDeleteResponse = any
  >
  extends DataServiceBase
  implements IDelete
{
  constructor(
    client: HttpClient,
    config: IAppConfig,
    notification: MessageService,
    private readonly modelConstructor: { new (): TModel },
    progressBar: ProgressBarService,
    serviceEndPoint: string
  ) {
    super(client, notification, progressBar, config, serviceEndPoint);
  }

  protected createModel(modelData: any): TModel {
    return toModel(this.modelConstructor, modelData);
  }

  protected toModels(response: any): TModel[] {
    if (Array.isArray(response)) {
      return response.map(m => this.createModel(m));
    }
    return [];
  }

  /**
   * Modern generic alternative to "add"-method (optional).
   * @param model
   * @param options
   */
  createNew?(model: TModelCreate, options?: { notifyUser: boolean }): Observable<IModel>;

  /**
   * Modern generic alternative to "change"-methods (optional).
   * @param model
   * @param options
   */
  updateExisting?(model: TModelUpdate, options?: { notifyUser: boolean }): Observable<TUpdateResponse>;

  add(model: TModel, options: { notifyUser: boolean } = { notifyUser: true }): Observable<any> {
    this.progressBar.start();
    return this.client.post(this.endpoint, JSON.stringify(model)).pipe(
      tap(() => {
        if (options && options.notifyUser) {
          this.notification.showSuccess();
        }
      }),
      finalize(() => {
        this.progressBar.stop();
      })
    );
  }

  change(model: TModel, options: { notifyUser: boolean } = { notifyUser: true }): Observable<any> {
    this.progressBar.start();
    return this.client.put(this.endpoint, JSON.stringify(model)).pipe(
      tap(() => {
        if (options && options.notifyUser) {
          this.notification.showSuccess();
        }
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  // TODO: get rid of this over time - use generics and "update" method instead
  changeAny(model: any, options: { notifyUser: boolean } = { notifyUser: true }): Observable<any> {
    this.progressBar.start();
    return this.client.put(this.endpoint, JSON.stringify(model)).pipe(
      tap(() => {
        if (options && options.notifyUser) {
          this.notification.showSuccess();
        }
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  delete(id: string): Observable<TDeleteResponse> {
    this.progressBar.start();
    return this.client.delete(`${this.endpoint}/${id}`).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  get(id: string): Observable<TModel> {
    this.progressBar.start();
    return this.client.get<TModel>(`${this.endpoint}/${id}`).pipe(
      map(item => this.createModel(item)),
      finalize(() => this.progressBar.stop())
    );
  }

  getWithoutId(): Observable<TModel> {
    this.progressBar.start();
    return this.client.get<TModel>(`${this.endpoint}`).pipe(
      map(item => this.createModel(item)),
      finalize(() => this.progressBar.stop())
    );
  }

  getDefaults(): Observable<TModel> {
    return this.client.get<TModel>(`${this.endpoint}/defaults`).pipe(map(res => this.createModel(res)));
  }

  getPagedList(filter: any, page: number, pageSize: number, order: string, descending: boolean): Observable<PagedResult<TModel>> {
    this.progressBar.start();
    filter = this.prepareFilter(filter);
    return this.client
      .post<PagedResult<TModel>>(`${this.endpoint}/page`, {
        filter: filter,
        page: page,
        pageSize: pageSize,
        orderBy: order,
        descending: descending,
      })
      .pipe(
        map(response => {
          let result = Object.assign(new PagedResult(), response);
          result.items = this.toModels(response.items);
          return result;
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getList(): Observable<TModel[]> {
    this.progressBar.start();
    return this.client.get<TModel[]>(this.endpoint).pipe(
      map(response => this.toModels(response)),
      finalize(() => this.progressBar.stop())
    );
  }

  getFilteredList(filter: any): Observable<TModel[]> {
    filter = this.prepareFilter(filter);

    this.progressBar.start();
    return this.client.post(`${this.endpoint}/filter`, JSON.stringify(filter)).pipe(
      map(items => {
        if (Array.isArray(items)) {
          return items.map(m => this.createModel(m));
        }
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getSelectionItems(filter?: any, defaultItem: boolean = true): Observable<SelectionItem<string>[]> {
    const result: Observable<TModel[]> = filter ? this.getFilteredList(filter) : this.getList();
    return result.pipe(
      map(items => {
        const selection = items.map(item => item.toSelectionItem());
        if (defaultItem) {
          selection.unshift(new SelectionItem());
        }
        return selection;
      })
    );
  }
}
